/**
 * Link classification + rewrite policy for brand skinning.
 *
 * Replaces the old rewriteLinks pass, which only handled tel/sms/mailto and
 * source-domain hrefs — 523 external links (138 social) used to survive
 * composition pointing at the SOURCE business's profiles. This engine:
 *   - positively rewrites template social links to the client's accounts;
 *   - maps template-internal paths onto the composed page map (multipage);
 *   - rewrites map links to the client's address;
 *   - neutralizes booking/source-business/unknown-external destinations with
 *     a data-pb-needs-review flag instead of leaving them live;
 *   - reports every anchor decision.
 *
 * Worker-safe: pure DOM-element functions, shared-module imports only.
 */

import { isSourceDomainUrl } from './source-domain-url.js';
import { phoneDigits as normalizePhoneDigits } from './occurrence-engine.js';
import { normalizeEmail } from './identity-pairs.js';

export const LINK_KINDS = Object.freeze({
  INTERNAL: 'internal',
  TEL: 'tel',
  SMS: 'sms',
  MAILTO: 'mailto',
  SOCIAL: 'social',
  MAP: 'map',
  BOOKING: 'booking',
  SOURCE_BUSINESS: 'source-business',
  INFRASTRUCTURE: 'infrastructure',
  EXTERNAL: 'external'
});

// Host tables (exported for tests). Social keys mirror the profiler's
// social object so client bindings line up 1:1.
export const SOCIAL_HOSTS = Object.freeze({
  facebook: ['facebook.com', 'fb.com', 'fb.me'],
  instagram: ['instagram.com', 'instagr.am'],
  linkedin: ['linkedin.com'],
  x: ['twitter.com', 'x.com'],
  youtube: ['youtube.com', 'youtu.be'],
  yelp: ['yelp.com'],
  tiktok: ['tiktok.com'],
  vimeo: ['vimeo.com'],
  pinterest: ['pinterest.com']
});

export const MAP_HOSTS = Object.freeze(['maps.app.goo.gl', 'maps.apple.com', 'waze.com']);

export const BOOKING_HOSTS = Object.freeze([
  'calendly.com',
  'acuityscheduling.com',
  'squareup.com',
  'square.site',
  'booksy.com',
  'vagaro.com',
  'housecallpro.com',
  'getjobber.com',
  'setmore.com',
  'schedulicity.com',
  'appointy.com',
  'simplybook.me'
]);

export const INFRASTRUCTURE_HOSTS = Object.freeze([
  'fonts.googleapis.com',
  'fonts.gstatic.com',
  'cdnjs.cloudflare.com',
  'unpkg.com',
  'cdn.jsdelivr.net',
  'schema.org',
  'w3.org'
]);

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function hostOf(href) {
  try {
    return new URL(href).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

function hostMatches(host, table) {
  return table.some((entry) => host === entry || host.endsWith(`.${entry}`));
}

export function socialPlatformForHref(href) {
  const host = hostOf(href);
  if (!host) return null;
  for (const [platform, hosts] of Object.entries(SOCIAL_HOSTS)) {
    if (hostMatches(host, hosts)) return platform;
  }
  return null;
}

function isMapHref(href, host) {
  if (hostMatches(host, MAP_HOSTS)) return true;
  return /google\.[a-z.]+/.test(host) && /\/maps/i.test(href);
}

/**
 * Classify an anchor href.
 *
 * @param {string} href
 * @param {{ sourceDomain?: string, sourceTerms?: string[] }} [opts]
 *   sourceTerms: lowercased identity strings (domain/name/email/phone) —
 *   an external href containing one is a source-business pointer.
 */
export function classifyHref(href, opts = {}) {
  const value = clean(href);
  const lower = value.toLowerCase();
  if (!value || value === '#' || lower.startsWith('javascript:')) {
    return { kind: LINK_KINDS.INTERNAL };
  }
  if (lower.startsWith('tel:')) return { kind: LINK_KINDS.TEL };
  if (lower.startsWith('sms:')) return { kind: LINK_KINDS.SMS };
  if (lower.startsWith('mailto:')) return { kind: LINK_KINDS.MAILTO };

  const isAbsolute = /^https?:\/\//i.test(value) || value.startsWith('//');
  if (!isAbsolute) {
    // Relative / root-relative / fragment: internal to the template site.
    return { kind: LINK_KINDS.INTERNAL };
  }

  if (opts.sourceDomain && isSourceDomainUrl(value, opts.sourceDomain)) {
    return { kind: LINK_KINDS.SOURCE_BUSINESS };
  }

  const host = hostOf(value.startsWith('//') ? `https:${value}` : value);
  const platform = socialPlatformForHref(value.startsWith('//') ? `https:${value}` : value);
  if (platform) return { kind: LINK_KINDS.SOCIAL, platform };
  if (isMapHref(lower, host)) return { kind: LINK_KINDS.MAP };
  if (hostMatches(host, BOOKING_HOSTS)) return { kind: LINK_KINDS.BOOKING };
  if (hostMatches(host, INFRASTRUCTURE_HOSTS)) return { kind: LINK_KINDS.INFRASTRUCTURE };
  if ((opts.sourceTerms || []).some((term) => lower.includes(term))) {
    return { kind: LINK_KINDS.SOURCE_BUSINESS };
  }
  return { kind: LINK_KINDS.EXTERNAL };
}

/**
 * Resolve a template-internal path against the composed page map.
 * Returns the composed href ('/', '/about/') or null when unmatched.
 *
 * @param {string} href                       e.g. "/about", "about.html", "../contact.html"
 * @param {Array<{name: string, slug: string}>} pageMap
 */
export function mapInternalHref(href, pageMap = []) {
  const value = clean(href);
  if (!value || value.startsWith('#')) return value || '#';
  const [pathPart, hashPart = ''] = value.split('#');
  const path = pathPart
    .replace(/^(\.\.\/)+|^\.\//, '')
    .replace(/^\//, '')
    .replace(/\/$/, '')
    .replace(/\.html?$/i, '')
    .toLowerCase();
  const hash = hashPart ? `#${hashPart}` : '';

  if (path === '' || path === 'index' || path === 'home') return `/${hash}`;

  const slugOf = (page) =>
    clean(page.slug) ||
    clean(page.name)
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  for (const page of pageMap) {
    const slug = slugOf(page);
    if (!slug) continue;
    if (path === slug || path.endsWith(`/${slug}`)) return `/${slug}/${hash}`;
    if (clean(page.name).toLowerCase() === path.replace(/-/g, ' ')) return `/${slug}/${hash}`;
  }
  return null;
}

// SK-5. `normalizePhoneDigits` and `normalizeEmail` were the THIRD and FOURTH
// copies of those two functions. They now come from the shared layer, so a
// change to how a phone is compared reaches the link policy too.
function needsReview(el, kind, report, sectionId, href, extra = {}) {
  el.setAttribute('href', '#');
  el.setAttribute('data-pb-needs-review', kind);
  if (report) {
    report.links.push({ sectionId, href, kind, action: 'neutralized', newHref: '#', ...extra });
  }
}

function recordLink(report, sectionId, href, kind, action, newHref, extra = {}) {
  if (!report) return;
  report.links.push({ sectionId, href, kind, action, newHref, ...extra });
}

/**
 * Apply the link policy to every anchor under root. Replaces rewriteLinks.
 *
 * @param {Element} root
 * @param {object} args
 * @param {object} args.sourceProfile
 * @param {object} args.clientProfile   (.social object keys match SOCIAL_HOSTS)
 * @param {string} args.sourceDomain
 * @param {Array<{name, slug}>} [args.pageMap]  composed pages for internal mapping
 * @param {object|null} [args.report]           skin-report sink
 * @param {string} [args.sectionId]
 * @param {{keepExternalLinks?: boolean}} [args.options]
 */
export function applyLinkPolicy(root, args) {
  const {
    sourceProfile = {},
    clientProfile = {},
    sourceDomain = '',
    pageMap = [],
    report = null,
    sectionId = '',
    options = {}
  } = args || {};

  const sourcePhones = new Set(
    (sourceProfile.phoneVariants || []).map(normalizePhoneDigits).filter(Boolean)
  );
  const clientPhone = normalizePhoneDigits(clientProfile.phone);
  const clientEmail = clean(clientProfile.email);
  const clientSocial = clientProfile.social || {};
  const clientMapLink = clean(clientProfile.mapLink);
  const clientAddress = clean(clientProfile.address);

  const sourceTerms = [
    sourceDomain,
    sourceProfile.businessName,
    ...(Array.isArray(sourceProfile.businessNameVariants)
      ? sourceProfile.businessNameVariants
      : []),
    sourceProfile.email,
    normalizeEmail(sourceProfile.email),
    ...(Array.isArray(sourceProfile.phoneVariants) ? sourceProfile.phoneVariants : []),
    ...Array.from(sourcePhones),
    ...Object.values(sourceProfile.social || {})
  ]
    .map((value) => clean(value).toLowerCase())
    .filter((value) => value.length >= 4);

  const phoneMatchesSource = (digits) => {
    if (!digits) return false;
    if (!sourcePhones.size) return true;
    if (sourcePhones.has(digits)) return true;
    return digits.length === 11 && digits.startsWith('1') && sourcePhones.has(digits.slice(1));
  };

  for (const link of root.querySelectorAll?.('a[href]') || []) {
    const href = clean(link.getAttribute('href'));
    const { kind, platform } = classifyHref(href, { sourceDomain, sourceTerms });

    switch (kind) {
      case LINK_KINDS.TEL:
      case LINK_KINDS.SMS: {
        const digits = normalizePhoneDigits(href);
        if (clientPhone && phoneMatchesSource(digits)) {
          const scheme = kind === LINK_KINDS.TEL ? 'tel' : 'sms';
          link.setAttribute('href', `${scheme}:${clientPhone}`);
          recordLink(report, sectionId, href, kind, 'rewritten', `${scheme}:${clientPhone}`);
        } else if (!clientPhone && phoneMatchesSource(digits)) {
          needsReview(link, 'phone', report, sectionId, href);
        }
        break;
      }
      case LINK_KINDS.MAILTO: {
        const sourceEmail = normalizeEmail(sourceProfile.email).toLowerCase();
        const linkEmail = normalizeEmail(href).toLowerCase();
        if (clientEmail && (!sourceEmail || linkEmail === sourceEmail)) {
          link.setAttribute('href', `mailto:${clientEmail}`);
          recordLink(report, sectionId, href, kind, 'rewritten', `mailto:${clientEmail}`);
        } else if (!clientEmail && sourceEmail && linkEmail === sourceEmail) {
          needsReview(link, 'email', report, sectionId, href);
        }
        break;
      }
      case LINK_KINDS.SOCIAL: {
        const clientUrl = clean(clientSocial[platform]);
        if (clientUrl) {
          link.setAttribute('href', clientUrl);
          recordLink(report, sectionId, href, kind, 'rewritten', clientUrl, { platform });
        } else {
          needsReview(link, `social-${platform}`, report, sectionId, href, { platform });
        }
        break;
      }
      case LINK_KINDS.MAP: {
        if (clientMapLink) {
          link.setAttribute('href', clientMapLink);
          recordLink(report, sectionId, href, kind, 'rewritten', clientMapLink);
        } else if (clientAddress) {
          const mapped = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clientAddress)}`;
          link.setAttribute('href', mapped);
          recordLink(report, sectionId, href, kind, 'rewritten', mapped);
        } else {
          needsReview(link, 'map', report, sectionId, href);
        }
        break;
      }
      case LINK_KINDS.BOOKING:
        needsReview(link, 'booking', report, sectionId, href);
        break;
      case LINK_KINDS.SOURCE_BUSINESS:
        link.setAttribute('href', '#');
        recordLink(report, sectionId, href, kind, 'neutralized', '#');
        break;
      case LINK_KINDS.INTERNAL: {
        if (!href || href.startsWith('#')) break;
        const mapped = mapInternalHref(href, pageMap);
        if (mapped !== null && mapped !== href) {
          link.setAttribute('href', mapped);
          recordLink(report, sectionId, href, kind, 'rewritten', mapped);
        } else if (mapped === null) {
          // A template-internal path with no composed counterpart must never
          // survive into an exported site.
          needsReview(link, 'internal-link', report, sectionId, href);
        }
        break;
      }
      case LINK_KINDS.INFRASTRUCTURE:
        recordLink(report, sectionId, href, kind, 'kept', href);
        break;
      case LINK_KINDS.EXTERNAL:
      default: {
        if (options.keepExternalLinks) {
          recordLink(report, sectionId, href, LINK_KINDS.EXTERNAL, 'kept', href);
        } else {
          needsReview(link, 'external', report, sectionId, href);
        }
        break;
      }
    }
  }

  for (const el of root.querySelectorAll?.('[action]') || []) {
    const action = clean(el.getAttribute('action'));
    if (isSourceDomainUrl(action, sourceDomain)) {
      el.setAttribute('action', '#');
      recordLink(report, sectionId, action, 'form-action', 'neutralized', '#');
    }
  }
}
